<template>
  <div>
    <div class="flex items-center justify-between mb-6">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Page Builds
      </h2>
      <button @click="$emit('create-build')" class="btn btn-primary btn-sm">
        New Page
      </button>
    </div>

    <div v-if="loading" class="flex justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
    </div>

    <div v-else-if="builds.length === 0" class="text-center py-12">
      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">No page builds yet</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Create a new page from a template to get started.
      </p>
    </div>

    <div v-else class="overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead>
          <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
            <th class="pb-3 pr-4">Slug</th>
            <th class="pb-3 pr-4">Template</th>
            <th class="pb-3 pr-4">Status</th>
            <th class="pb-3 pr-4">Created</th>
            <th class="pb-3">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
          <tr
            v-for="build in builds"
            :key="build.id"
            class="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
            @click="$emit('select-build', build)"
          >
            <td class="py-3 pr-4 font-mono text-gray-900 dark:text-gray-100">
              {{ build.target_slug }}
            </td>
            <td class="py-3 pr-4 text-gray-600 dark:text-gray-400">
              {{ build.template_name }}
            </td>
            <td class="py-3 pr-4">
              <span :class="statusClass(build.build_status)" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium">
                {{ build.build_status }}
              </span>
            </td>
            <td class="py-3 pr-4 text-gray-500 dark:text-gray-400">
              {{ formatDate(build.created_at) }}
            </td>
            <td class="py-3">
              <div class="flex gap-2" @click.stop>
                <button
                  v-if="build.build_status === 'review'"
                  @click="$emit('approve-build', build)"
                  class="text-xs text-green-600 hover:text-green-700"
                >
                  Approve
                </button>
                <button
                  v-if="build.build_status === 'review'"
                  @click="$emit('reject-build', build)"
                  class="text-xs text-red-600 hover:text-red-700"
                >
                  Reject
                </button>
                <button
                  v-if="build.build_status === 'approved'"
                  @click="$emit('publish-build', build)"
                  class="text-xs text-primary-600 hover:text-primary-700"
                >
                  Publish
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getPageBuilds } from '~/javascript/api/page_builds'

const props = defineProps({
  pageTemplateId: { type: String, required: true }
})

defineEmits(['create-build', 'select-build', 'approve-build', 'reject-build', 'publish-build'])

const builds = ref([])
const loading = ref(true)

const statusClass = (status) => {
  const classes = {
    draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    generating: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    review: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    approved: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    published: 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300',
    rejected: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
  }
  return classes[status] || classes.draft
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const fetchBuilds = async () => {
  loading.value = true
  try {
    const { data } = await getPageBuilds(props.pageTemplateId)
    builds.value = data
  } finally {
    loading.value = false
  }
}

onMounted(fetchBuilds)

defineExpose({ fetchBuilds })
</script>
